import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import {
  inheritingRolesFor,
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryPlacementRegistryQuery,
  type LibraryAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { isPartsContent, toDraft, toPartsContent } from "@/lib/assetContent";
import {
  copyableParts,
  languageProductName,
  rulesLanguages,
} from "@/lib/rulesContent";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { createFieldErrors } from "@/ui/library/NewAssetDialog";
import { LoadingState } from "@/ui/states";

import { NamedReadFailureState } from "./NamedReadFailure";

/**
 * Create an editable copy of a Rule — one purpose, named content (#910).
 *
 * WHY THE GENERIC DIALOG COULD NOT STAY. `NewAssetDialog` carries a Type picker
 * across nine families and `PartsEditor` beneath it, so copying the seeded Rule
 * opened on a free-text "Part key" input reading `en/base_rules` and a JSON
 * textarea holding base canon's four-hundred-entry substitutions document. Both
 * are exactly what this ticket removes from the Rules surface, and leaving them
 * on the ONE route a clinician must take to get an editable Rule would have made
 * the named editor unreachable without first meeting them.
 *
 * SO THIS DIALOG ASKS FOR A NAME AND NOTHING ELSE, and the clinician edits the
 * content afterwards in the named editor, which is the post-create step the
 * canonical journey describes. There is no coordinate field: a Rule is a
 * FIXED-placement family, its files land at base canon's own paths, and
 * `POST /v1/library/assets` refuses a coordinate on this type outright.
 *
 * A COPY IS NOT A DUPLICATE, and `copyableParts` states the whole reason. The
 * prompt documents come across because they REPLACE their base file; the
 * documents that merge onto base start inherited, because copying them would
 * write four hundred entries saying what base already says — the redundant asset
 * the S7 contract's own "unchanged base content is inherited and compared" rules
 * out. The dialog says so on screen rather than leaving a clinician to discover
 * it in the tables.
 *
 * COPY-ONLY, DELIBERATELY. There is no from-scratch arm here, and its absence is
 * a fact about the family rather than an omission: a Rule that carries no
 * `he/base_rules` and no `en/base_rules` is refused at create
 * (`library.validate_required_languages`), so a blank one is not a state the
 * service has. `NewAssetDialog` remains the from-scratch route for a clinician
 * who genuinely wants to type a Rule from nothing; the seeded copy — the journey
 * the ticket names — comes through here.
 */

const NAME_MAX_LENGTH = 60;

/**
 * What a refusal computed in the BROWSER says, so nothing is ever swallowed.
 *
 * `NewMainConcernDialog`'s rule, for its reason: this dialog hands the copied
 * parts to `toPartsContent` before publishing, and that check can refuse for
 * reasons no field on screen owns. A silent `return` there is a Create button
 * that does nothing at all.
 */
const REFUSED_CONTENT =
  "This rule's documents could not be prepared, so nothing was created.";

export function NewRulesDialog({
  copyFrom,
  onClose,
}: {
  /** The Rule being copied. Required: this dialog has no from-scratch arm. */
  copyFrom: LibraryAsset;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const nameErrorId = useId();

  const [name, setName] = useState(`${copyFrom.name} (copy)`);
  const [blocked, setBlocked] = useState<string | null>(null);

  const source = useAssetVersionQuery(copyFrom.id, copyFrom.latestVersion);
  // WHICH DOCUMENTS MERGE ONTO BASE IS THE SERVICE'S ANSWER, never this
  // dialog's: `FamilyPolicy.placement.parts[].mergeStrategy` off the placement
  // registry. Empty is NOT a usable answer — an unread registry is
  // indistinguishable from "no document merges" — so `registryUnresolved` below
  // refuses the copy rather than falling back to a verbatim one.
  const registry = useLibraryPlacementRegistryQuery();
  const inheritingRoles = inheritingRolesFor(registry.data, "language_rules");
  // Cancel-then-refetch, never a bare `refetch()`: the state that most needs
  // this control is the paused one, and that is the state a bare refetch
  // cannot clear.
  const retrySource = useRetryPausedQuery(
    queryKeys.assetVersion(copyFrom.id, copyFrom.latestVersion),
  );

  const stored =
    source.isSuccess && isPartsContent(source.data.content)
      ? source.data.content
      : null;
  // What the copy will actually carry, named the way the editor names it — so
  // the dialog states the languages and documents rather than showing their
  // keys. `""` as the asset type: this summary is about the SHAPE of the copy,
  // and product names for roles are the editor's own copy map.
  const copied =
    stored === null ? [] : copyableParts(toDraft(stored), inheritingRoles);
  const languages = rulesLanguages(copied, "");
  const inheritedCount =
    stored === null ? 0 : toDraft(stored).length - copied.length;

  const fieldErrors = create.isError ? createFieldErrors(create.error) : {};
  const generalMessage =
    blocked ??
    (create.isError && Object.keys(fieldErrors).length === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null);

  const busy = create.isPending;
  const unread = source.isPaused || source.isError;
  // Cancel-then-refetch for the registry too, and for the same reason: a held
  // retry is exactly the state a bare refetch cannot clear.
  const retryRegistry = useRetryPausedQuery(queryKeys.libraryPlacementRegistry);
  const registryUnread = registry.isPaused || registry.isError;
  // THE REGISTRY IS A PREREQUISITE, NOT A HINT, and treating its absence as
  // "copy everything" was the defect. `inheritingRolesFor` answers `[]` while
  // the read is pending or failed, which is indistinguishable from "no document
  // merges" — so a Create pressed in that window copied base canon's whole
  // substitutions table into the new asset and published exactly the redundant
  // overlay this dialog exists to prevent. That is shared clinical content and
  // every adopting project exports it, so refusing to create is the cheaper
  // failure: a copy not made is a button pressed again, and a copy made wrong
  // is an immutable version.
  const registryUnresolved = !registry.isSuccess;

  function submit() {
    setBlocked(null);
    if (stored === null) {
      setBlocked(
        `The Editor Service returned ${copyFrom.name} in a form this editor does not recognise, so nothing was created.`,
      );
      return;
    }
    const content = toPartsContent(copyableParts(toDraft(stored), inheritingRoles));
    if (!content.ok) {
      setBlocked(content.problems[0]?.message ?? REFUSED_CONTENT);
      return;
    }
    create.mutate(
      {
        assetType: "language_rules",
        name: name.trim(),
        content: content.content,
        note: `Copied from "${copyFrom.name}" at ${copyFrom.latestVersion}.`,
        // NO `placementCoordinate`. A Rule is a fixed-placement family whose
        // files land at base canon's own paths, and the service refuses a
        // client-chosen coordinate on this type (`validate_coordinate`).
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(`/library/rules/${created.id}`);
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title="Create editable copy"
        description="Creates your own rule starting from this one, in every language it carries. The original stays exactly as it is."
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Tooltip
              content={
                registryUnresolved
                  ? "Waiting for the Editor Service to say which of this rule's documents merge onto the base library."
                  : "Create this copy"
              }
            >
              <Button
                disabled={
                  busy || name.trim() === "" || stored === null || registryUnresolved
                }
                onClick={submit}
              >
                Create copy
              </Button>
            </Tooltip>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label="Name"
            description="Short name for this rule. Its content is edited afterwards, from the rule's own page."
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  aria-invalid={fieldErrors.name !== undefined || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.name ? nameErrorId : undefined,
                  )}
                  onChange={(event) => setName(event.target.value)}
                />
                {fieldErrors.name ? (
                  <FieldError id={nameErrorId} message={fieldErrors.name} />
                ) : null}
              </>
            )}
          </Field>

          {(source.isPending && !unread) ||
          (registry.isPending && !registryUnread) ? (
            <LoadingState
              label={`Loading content from ${copyFrom.name}`}
              rows={2}
            />
          ) : null}

          {unread ? (
            <NamedReadFailureState
              failure={source.isPaused ? source.failureReason : source.error}
              isPaused={source.isPaused}
              retry={retrySource}
              subject="Asset content"
              title="This rule's content could not be read"
              description={`The Editor Service did not return ${copyFrom.name} at ${copyFrom.latestVersion}, so there is nothing to copy from yet.`}
              deniedDescription="You are not authorized to read this rule's content, so it cannot be copied."
              offlineDescription={`${copyFrom.name} at ${copyFrom.latestVersion} could not be reached, so there is nothing to copy from yet. It loads on its own when the connection returns.`}
            />
          ) : null}

          {registryUnread ? (
            // A REPORTED FAILURE, not a disabled button with a tooltip. Without
            // this read the dialog cannot tell which documents merge, and
            // guessing in either direction is worse than saying so: copying
            // everything writes the redundant overlay, and copying nothing
            // would publish a rule with no rules text.
            <NamedReadFailureState
              failure={registry.isPaused ? registry.failureReason : registry.error}
              isPaused={registry.isPaused}
              retry={retryRegistry}
              subject="Placement registry"
              title="How this rule's documents compose could not be read"
              description="The Editor Service did not report which of a rule's documents merge onto the base library, and copying without that would either duplicate the base library's substitutions or drop this rule's own text."
              deniedDescription="You are not authorized to read the Editor Service's placement policy, so this rule cannot be copied here."
              offlineDescription="Which of a rule's documents merge onto the base library could not be read. It loads on its own when the connection returns."
            />
          ) : null}

          {languages.length > 0 && registry.isSuccess ? (
            // WHAT THE COPY WILL CARRY, in the editor's own words. The generic
            // dialog answered this by rendering every part key and document; a
            // clinician needs the shape, not the keys, and the content itself is
            // one click away in the editor this dialog navigates to.
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              <p>The copy carries:</p>
              <ul className="mt-1 list-inside list-disc">
                {languages.map((view) => (
                  <li key={view.language}>
                    {languageProductName(view.language)} —{" "}
                    {view.prompts.length}{" "}
                    {view.prompts.length === 1 ? "prompt" : "prompts"}
                    {view.hasSubstitutions ? ", substitutions" : null}
                    {view.structured.length > 0
                      ? `, ${view.structured.length} clinical engine ${view.structured.length === 1 ? "document" : "documents"}`
                      : null}
                  </li>
                ))}
              </ul>
              {inheritedCount > 0 ? (
                // Stated rather than silent: a clinician who expected a byte
                // duplicate needs to know why the substitution tables open with
                // nothing marked as changed, and that the runtime is unaffected.
                <p className="mt-2">
                  Its substitution tables start out inherited from the base
                  library, so the copy behaves exactly as the original does until
                  you change an entry. Nothing is lost — every inherited entry is
                  shown in the tables.
                </p>
              ) : null}
            </div>
          ) : null}

          {generalMessage ? (
            <Notice tone="destructive" role="alert">
              {generalMessage}
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
